import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { FiArrowUpRight, FiExternalLink, FiCode, FiLayout, FiShoppingCart, FiUsers, FiCamera, FiHeart, FiTruck, FiHome, FiShield, FiFileText } from 'react-icons/fi';
import SEO from '../components/SEO';
import './Portfolio.css';

const AnimatedText = ({ children, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const AnimatedSection = ({ children, className, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Portfolio = () => {
  const projects = [
    {
      id: 'hometaxpro',
      title: 'HomeTaxPro',
      category: 'Web Application',
      categoryIcon: <FiHome />,
      description: 'AI-powered property tax protest platform. Homeowners scan their address against county appraisal + comps, subscribe, and download a filing-ready protest PDF generated by Claude.',
      image: 'https://silverfoxmedia.s3.amazonaws.com/htphomepageonmac.png',
      mobileImage:
        "https://silverfoxmedia.s3.amazonaws.com/HomeTaxPro%20%E2%80%94%20America%27s%20%231%20Property%20Tax%20Protest%20Service%202-portrait.png",
      url: 'https://hometaxpro.com/',
      caseStudySlug: 'hometaxpro',
      technologies: ['React', 'Node.js', 'MongoDB', 'Anthropic Claude', 'Stripe', 'SendGrid'],
      color: '#5FE8B0'
    },
    {
      id: 'aishadow',
      title: 'AI Shadow',
      category: 'Enterprise SaaS',
      categoryIcon: <FiShield />,
      description: 'Enterprise AI governance and DLP platform with multi-layer enforcement across the browser layer, backend, and network — plus a patent-pending actuarial risk-scoring engine.',
      image: 'https://silverfoxmedia.s3.amazonaws.com/aishadowmacbook.png',
      mobileImage:
        'https://silverfoxmedia.s3.amazonaws.com/AI%20Shadow%E2%84%A2%20%20Enterprise%20AI%20Governance%20%26%20DLP-portrait.png',
      url: 'https://aishadow.ai/',
      caseStudySlug: 'aishadow',
      technologies: ['HTML5', 'CSS3', 'JavaScript', 'GA4 Consent Mode v2', 'JSON-LD', 'SendGrid'],
      color: '#10B981'
    },
    {
      id: 6,
      title: 'MyMedicalCabinet',
      category: 'Healthcare',
      categoryIcon: <FiHeart />,
      description: 'A secure digital health records platform helping users store, manage, and access their medical records, medications, and doctor information.',
      image: 'https://silverfoxmedia.s3.amazonaws.com/MMCscreenhp-front.png',
      mobileImage:
        'https://silverfoxmedia.s3.amazonaws.com/Screenshot%202026-07-13%20at%2010.51.41%E2%80%AFAM-portrait.png',
      url: 'https://mymedicalcabinet.com/',
      caseStudySlug: 'mymedicalcabinet',
      technologies: ['React', 'Node.js', 'MongoDB', 'Epic FHIR', 'Anthropic Claude', 'Stripe Connect'],
      color: '#3B82F6'
    },
    {
      id: 'asahealthmd',
      title: 'ASA Health MD',
      category: 'Medical / Wellness',
      categoryIcon: <FiHeart />,
      description: 'Full rebuild and rebrand of a Plano, Texas medical aesthetics and wellness practice (formerly advsurgicalarts.com). Beauty · Brain · Bio — three service categories spanning aesthetics, cosmetic surgery, and wellness, with SendGrid-wired inquiry intake.',
      image: 'https://asahealthmd.s3.amazonaws.com/asahealthmdscreenhp-front.png',
      mobileImage: 'https://asahealthmd.s3.amazonaws.com/asahealthmdscreenhp-portrait.png',
      url: 'https://asahealthmd.com/',
      caseStudySlug: 'asahealthmd',
      technologies: ['Next.js 14', 'TypeScript', 'Tailwind CSS', 'Node.js', 'Express', 'MongoDB Atlas', 'SendGrid'],
      color: '#014D7F'
    },
    {
      id: 1,
      title: 'Pagomigo',
      category: 'Fintech / Digital Wallet',
      categoryIcon: <FiCode />,
      description: 'A digital wallet with P2P transfers, issued debit cards, QR / NFC payments, and bill pay — with full KYC onboarding via Persona and bank linking via Plaid.',
      image: 'https://silverfoxmedia.s3.amazonaws.com/pagomigohpv2-front.png',
      url: 'https://www.pagomigo.com/',
      caseStudySlug: 'pagomigo',
      technologies: ['React', 'Node.js', 'MongoDB', 'Persona KYC', 'Plaid', 'Unit', 'Stripe'],
      color: '#1E40AF'
    },
    {
      id: 2,
      title: 'Grace to Change',
      category: 'Non-Profit',
      categoryIcon: <FiUsers />,
      description: 'Full rebuild for a North Texas addiction-recovery nonprofit — replaced their aging WordPress site with a fast public site and a custom admin CMS the team runs themselves. Received a $100K post-launch grant.',
      image: 'https://silverfoxmedia.s3.amazonaws.com/GTCnewsitehomepage2-front.png',
      url: 'https://gracetochange.org/',
      caseStudySlug: 'gracetochange',
      technologies: ['React', 'Node.js', 'Express', 'MongoDB', 'Anthropic Claude', 'AWS S3', 'Qgiv'],
      color: '#C0603F'
    },
    {
      id: 3,
      title: 'NTX Luxury Van Rentals',
      category: 'Transportation',
      categoryIcon: <FiShoppingCart />,
      description: 'Premium van rental service website with booking system and fleet showcase.',
      image: '/images/portfolio/ntxvanrentals.webp',
      url: 'https://ntxluxuryvanrentals.com/',
      technologies: ['React', 'Booking System', 'Google Maps', 'Stripe'],
      color: '#F59E0B'
    },
    {
      id: 4,
      title: 'GymCrush',
      category: 'Dating App',
      categoryIcon: <FiLayout />,
      description: 'A unique dating platform connecting fitness enthusiasts at their local gyms.',
      image: '/images/portfolio/gymcrush.webp',
      url: 'https://gymcrush.io/',
      technologies: ['React Native', 'Node.js', 'MongoDB', 'Real-time Chat'],
      color: '#EC4899'
    },
    {
      id: 5,
      title: 'SexySelfies',
      category: 'Creator Platform',
      categoryIcon: <FiCamera />,
      description: 'A mobile-first creator monetization platform with a custom WebRTC-to-RTMP live streaming stack, real-time Socket.io chat, algorithm-driven discovery feed, and a self-serve in-feed ad auction.',
      image: 'https://sexyselfies-content.s3.amazonaws.com/SSscreenhp-front.png',
      mobileImage:
        'https://sexyselfies-content.s3.amazonaws.com/Screenshot%202026-07-13%20at%2011.05.00%E2%80%AFAM-portrait.png',
      url: 'https://sexyselfies.com/',
      caseStudySlug: 'sexyselfies',
      technologies: ['React 19', 'Node.js', 'MongoDB', 'Socket.io', 'WebRTC (MediaSoup)', 'AWS IVS', 'Stripe'],
      color: '#14B8A6',
      // Card temporarily hidden from the /portfolio grid without
      // removing the data. The /portfolio/sexyselfies case study
      // page stays reachable at its direct URL for now.
      hidden: true
    },
    {
      id: 7,
      title: 'Baby Bunny Resale',
      category: 'E-Commerce',
      categoryIcon: <FiShoppingCart />,
      description: 'An auction platform for buying and selling children\'s items, creating a community marketplace for parents and caregivers.',
      image: '/images/portfolio/babybunnyresale-macbook.webp',
      url: 'https://babybunnyresale.com/',
      technologies: ['React', 'Node.js', 'MongoDB', 'Auction System', 'Stripe'],
      color: '#F472B6',
      caseStudySlug: 'babybunnyresale'
    },
    {
      id: 8,
      title: 'OneTouchAuto',
      category: 'Automotive AI',
      categoryIcon: <FiTruck />,
      description: 'An AI-powered automotive platform streamlining dealership operations and enhancing the car buying experience.',
      image: '/images/portfolio/onetouchauto.webp',
      url: 'https://onetouchauto.ai/',
      technologies: ['React', 'Node.js', 'MongoDB', 'AI/ML', 'OpenAI'],
      color: '#8B5CF6'
    },
  ];

  return (
    <>
      <SEO
        title="Our Portfolio"
        description="Products, platforms and websites we've designed and built: fintech, marketplaces, healthcare, SaaS, nonprofits and Palantir Foundry data applications."
        url="/portfolio"
        keywords="web development portfolio, React projects, custom websites, web applications, Dallas web design"
      />

      {/* HERO SECTION */}
      <section className="portfolio-hero">
        <div className="portfolio-hero__bg">
          <div className="portfolio-hero__gradient-orb portfolio-hero__gradient-orb--1" />
          <div className="portfolio-hero__gradient-orb portfolio-hero__gradient-orb--2" />
          <div className="portfolio-hero__grid-overlay" />
        </div>

        <div className="container">
          <motion.div
            className="portfolio-hero__content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.p
              className="portfolio-hero__label"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              Our Work
            </motion.p>

            <motion.h1
              className="portfolio-hero__title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Projects That <span className="text-gradient">Inspire</span>
            </motion.h1>

            <motion.p
              className="portfolio-hero__subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              Every project tells a story of collaboration, innovation, and results.
              Explore how we've helped businesses transform their digital presence.
            </motion.p>

            <motion.div
              className="portfolio-hero__stats"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              <div className="portfolio-hero__stat">
                <span className="portfolio-hero__stat-value">500+</span>
                <span className="portfolio-hero__stat-label">Projects Delivered</span>
              </div>
              <div className="portfolio-hero__stat-divider" />
              <div className="portfolio-hero__stat">
                <span className="portfolio-hero__stat-value">100%</span>
                <span className="portfolio-hero__stat-label">Client Satisfaction</span>
              </div>
              <div className="portfolio-hero__stat-divider" />
              <div className="portfolio-hero__stat">
                <span className="portfolio-hero__stat-value">12+</span>
                <span className="portfolio-hero__stat-label">Years Experience</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <div className="portfolio-hero__scroll">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
          >
            <span>Scroll to explore</span>
            <div className="portfolio-hero__scroll-line">
              <div className="portfolio-hero__scroll-dot" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROJECTS SECTION */}
      <section className="portfolio-projects">
        <div className="container">
          <AnimatedText className="portfolio-projects__header">
            <p className="section-label">Featured Work</p>
            <h2 className="section-title">
              Shipped <span className="text-gradient">Projects</span>
            </h2>
          </AnimatedText>

          <div className="portfolio-projects__grid">
            {projects.filter((p) => !p.hidden).map((project, index) => {
              // Cards with a caseStudySlug render as an internal Link
              // to the deep-dive page; every other card keeps the
              // original external <a target="_blank"> to the live site.
              // This is why the card body is extracted into cardInner
              // once — same JSX renders inside either wrapper.
              const hasCaseStudy = Boolean(project.caseStudySlug);
              const ctaLabel = hasCaseStudy ? 'View Case Study' : 'Visit Site';
              const CtaIcon = hasCaseStudy ? FiFileText : FiExternalLink;

              const cardInner = (
                <>
                  <div className="portfolio-project__image-container">
                    <div className="portfolio-project__image-wrapper">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="portfolio-project__image"
                      />
                    </div>
                    {project.mobileImage && (
                      <img
                        src={project.mobileImage}
                        alt={`${project.title} on mobile`}
                        className="portfolio-project__mobile"
                        loading="lazy"
                      />
                    )}
                    <div className="portfolio-project__overlay">
                      <span className="portfolio-project__view-btn">
                        <CtaIcon />
                        {ctaLabel}
                      </span>
                    </div>
                    <div className="portfolio-project__badge">
                      {project.categoryIcon}
                      {project.category}
                    </div>
                  </div>

                  <div className="portfolio-project__content">
                    <h3 className="portfolio-project__title">
                      {project.title}
                      <FiArrowUpRight className="portfolio-project__arrow" />
                    </h3>
                    <p className="portfolio-project__description">
                      {project.description}
                    </p>
                    <div className="portfolio-project__tech">
                      {project.technologies.map((tech) => (
                        <span key={tech} className="portfolio-project__tech-tag">
                          {tech}
                        </span>
                      ))}
                    </div>
                    {hasCaseStudy && (
                      <span className="portfolio-project__case-study-cta">
                        <FiFileText />
                        View Case Study <FiArrowUpRight />
                      </span>
                    )}
                  </div>

                  <div className="portfolio-project__glow" />
                </>
              );

              return (
                <AnimatedSection
                  key={project.id}
                  delay={index * 0.15}
                  className="portfolio-project-wrapper"
                >
                  {hasCaseStudy ? (
                    <Link
                      to={`/portfolio/${project.caseStudySlug}`}
                      className="portfolio-project"
                      style={{ '--project-color': project.color }}
                    >
                      {cardInner}
                    </Link>
                  ) : (
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="portfolio-project"
                      style={{ '--project-color': project.color }}
                    >
                      {cardInner}
                    </a>
                  )}
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="portfolio-cta">
        <div className="portfolio-cta__bg">
          <div className="portfolio-cta__gradient" />
        </div>
        <div className="container">
          <AnimatedText className="portfolio-cta__content">
            <h2 className="portfolio-cta__title">
              Ready to Start Your <span className="text-gradient">Project</span>?
            </h2>
            <p className="portfolio-cta__text">
              Let's collaborate and build something extraordinary together.
            </p>
            <a href="/contact" className="btn-glow btn-glow--large">
              <span>Get In Touch</span>
              <FiArrowUpRight />
            </a>
          </AnimatedText>
        </div>
      </section>
    </>
  );
};

export default Portfolio;
